import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar"; 
import Overview from "./Overview"; 
import AccountPage from "./AccountPage"; 
import TopNav from "./TopNav";
import CertificatePage from "./CertificatePage";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { format, startOfMonth, isAfter } from "date-fns";
import RoleManager from "./RoleManager";
import MemberControlPage from "./MemberControlPage";
import BillingPage from "./BillingPage";


// --- HIGH-PRECISION INDUSTRIAL SKELETON ---
const DashboardSkeleton = () => (
  <div className="p-8 space-y-8 animate-pulse">
    {/* Page Title */}
    <div className="h-6 w-48 bg-slate-200" />
    
    {/* Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-28 bg-slate-100 border border-slate-200" />
      ))}
    </div>
    
    {/* Main Registry Table Area */}
    <div className="h-[450px] bg-slate-50 border border-slate-200 w-full" />
  </div>
);
const DashboardLayout = ({ profile }) => {
  const location = useLocation();
  
  // --- GLOBAL STATES ---
  const [currentUser, setCurrentUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30D'); // Default range

  // 1. AUTH LISTENER
  useEffect(() => {
    setLoading(true);
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        console.log("✅ System Auth Verified:", user.uid);
      } else {
        console.log("❌ No Active Session.");
        setLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. GLOBAL DATA FETCHING (Firebase Logs)
  useEffect(() => {
    if (!currentUser?.uid) return;

    setLoading(true);

    const getStartTime = (range) => {
      const now = new Date();
      switch (range) {
        case '24H': return new Date(now.getTime() - 24 * 60 * 60 * 1000);
        case '7D':  return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        case '30D': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        case 'ALL': return new Date(0); 
        default:    return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }
    };

    const startTime = getStartTime(timeRange);

    const q = query(
      collection(db, "usage_logs"),
      where("userId", "==", currentUser.uid),
      where("timestamp", ">=", startTime),
      orderBy("timestamp", "desc"),
      limit(50) // Increased limit slightly for better overview charts
    );

    const unsubscribeLogs = onSnapshot(q, (snapshot) => {
      const logsArray = snapshot.docs.map(doc => {
        const data = doc.data();
        const jsDate = data.timestamp?.toDate ? data.timestamp.toDate() : new Date();
        
        return {
          id: doc.id,
          ...data,
          computeTime: data.performance?.duration || 0,
          efficiency: data.performance?.efficiency_score || 0,
          fullTimestamp: jsDate,
          dateLabel: format(jsDate, "dd/MM HH:mm"), 
        };
      });

      setLogs(logsArray.reverse());
      // Smooth transition for high-end feel
      setTimeout(() => setLoading(false), 600); 
    }, (error) => {
      setLoading(false);
      console.error("❌ Data Fetch Error:", error);
    });

    return () => unsubscribeLogs();
  }, [currentUser?.uid, location.state?.triggerRefresh, timeRange]);

  // 3. GLOBAL STATS CALCULATION
  const stats = useMemo(() => {
    if (logs.length === 0) return { monthly: 0, avgLatency: 0, totalCompute: 0, avgEfficiency: 0 };
    
    const totalTime = logs.reduce((acc, curr) => acc + (Number(curr.computeTime) || 0), 0);
    const totalEff = logs.reduce((acc, curr) => acc + (Number(curr.efficiency) || 0), 0);
    
    return {
      monthly: logs.filter(l => isAfter(l.fullTimestamp, startOfMonth(new Date()))).length,
      avgLatency: (totalTime / logs.length).toFixed(2),
      totalCompute: totalTime.toFixed(1),
      avgEfficiency: (totalEff / logs.length).toFixed(1)
    };
  }, [logs]);

  // Unified Props Object to pass to child routes
  const sharedProps = {
    profile,
    currentUser,
    logs,
    stats,
    loading,
    timeRange,
    setTimeRange // Allow Overview to change the range
  };

  return (
    <div className="relative min-h-screen bg-slate-50 flex overflow-hidden font-sans">
      
      {/* 1. SIDEBAR */}
      <Sidebar isOrg={profile?.profileType === "organization"} profile={profile} />

      {/* 2. CONTENT AREA */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        <TopNav profile={profile} /> 
        
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* We use loading here to show the Skeleton for ALL pages while data syncs */}
          {loading ? (
            <DashboardSkeleton />
          ) : (
            <Routes>
              <Route path="/" element={<Overview {...sharedProps} />} />
              <Route path="/certificate" element={<CertificatePage {...sharedProps} />} />
              <Route path="/account" element={<AccountPage {...sharedProps} />} />
                            <Route path="billing" element={<BillingPage {...sharedProps} />} />

              <Route path="/account/rolemanager" element={<RoleManager {...sharedProps} />} />
              <Route path="/account/rolemanager/member/:memberId" element={<MemberControlPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;